import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Bot,
  Sparkles,
  PhoneCall,
  Phone,
  Cable,
  BookOpen,
  Puzzle,
  Webhook,
  Workflow,
  BarChart3,
  Gauge,
  MessageCircle,
  MessagesSquare,
  CreditCard,
  Settings,
  ShieldCheck,
  Tags,
  Timer,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { env } from "@/lib/env";
import { hasMinRole, useAuthStore } from "@/features/auth/store";
import { useWhatsAppUnread } from "@/features/whatsapp/alerts";
import { useUIStore } from "@/store/ui";

type NavItem = {
  path: string;
  label: string;
  icon: LucideIcon;
  minRole: "member" | "manager" | "admin" | "owner";
  /** Hidden from a customer login: this page is the tenant's own business —
   *  their provider keys, their team, their wallet, their other customers, or
   *  infrastructure a customer neither owns nor pays for.
   *
   *  Everything else is shared, because a customer gets the same dashboard as
   *  the tenant responsible for them (Peter, 2026-07-28), with their own rows
   *  in it. The flag is a deny-list rather than an allow-list for that reason:
   *  a page listed here is one somebody decided to withhold. */
  tenantOnly?: boolean;
  /** The other direction, and rare: a page only a customer has. A tenant user
   *  has no plan of their own to look at, so there is nothing to share. */
  customerOnly?: boolean;
  /** The /admin area: shown to superusers only, whatever their role. */
  superuserOnly?: boolean;
};

type NavGroup = {
  label: string | null;
  items: NavItem[];
};

const NAV_GROUPS: NavGroup[] = [
  {
    label: null,
    items: [
      { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, minRole: "member" },
    ],
  },
  {
    label: "Build",
    items: [
      { path: "/agents", label: "Agents", icon: Bot, minRole: "member" },
      { path: "/assistants", label: "Assistants", icon: Sparkles, minRole: "member" },
      { path: "/flows", label: "Flows", icon: Workflow, minRole: "member" },
      // Template Library removed from the tenant sidebar (Vapi has none). The
      // quickstart templates are predefined by the site admin in Django admin
      // and surface only inside the Create Assistant popup. Route/code kept.
      { path: "/knowledge-bases", label: "Knowledge Bases", icon: BookOpen, minRole: "member" },
    ],
  },
  {
    label: "Connect",
    items: [
      { path: "/phone-numbers", label: "Phone Numbers", icon: Phone, minRole: "admin" },
      // The tenant's telephony and provider accounts: SIP credentials, the
      // Meta WhatsApp business connection, third-party API keys.
      { path: "/sip-trunks", label: "SIP Trunks", icon: Cable, minRole: "admin", tenantOnly: true },
      { path: "/whatsapp", label: "WhatsApp", icon: MessageCircle, minRole: "admin", tenantOnly: true },
      { path: "/integrations", label: "Integrations", icon: Puzzle, minRole: "member", tenantOnly: true },
      { path: "/webhooks", label: "Webhooks", icon: Webhook, minRole: "member" },
    ],
  },
  {
    label: "Observe",
    items: [
      { path: "/calls", label: "Calls", icon: PhoneCall, minRole: "member" },
      // WhatsApp threads arrive on the tenant's business number, which no
      // customer owns yet — until numbers can be assigned, the inbox is theirs.
      { path: "/conversations", label: "Conversations", icon: MessagesSquare, minRole: "member", tenantOnly: true },
      { path: "/analytics", label: "Analytics", icon: BarChart3, minRole: "member" },
      // Pipeline diagnostics across every call the platform handles.
      { path: "/latency", label: "Latency", icon: Gauge, minRole: "admin", tenantOnly: true },
    ],
  },
  {
    label: "Manage",
    items: [
      // The tenant's own business: who its customers are, what it charges
      // them, and what it owes for them.
      { path: "/customers", label: "Customers", icon: Users, minRole: "admin", tenantOnly: true },
      { path: "/customer-plans", label: "Customer Plans", icon: Tags, minRole: "admin", tenantOnly: true },
      { path: "/billing", label: "Billing", icon: CreditCard, minRole: "admin", tenantOnly: true },
      // The customer's side of that same relationship: the plan they were sold
      // and the minutes left on it.
      { path: "/my-plan", label: "My Plan", icon: Timer, minRole: "member", customerOnly: true },
    ],
  },
];

const SECONDARY_NAV: NavItem[] = [
  { path: "/settings", label: "Settings", icon: Settings, minRole: "member" },
  // The platform admin area — a separate shell at /admin.
  { path: "/admin", label: "Admin", icon: ShieldCheck, minRole: "member", superuserOnly: true },
];

export default function Sidebar() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? "member";
  const isCustomer = role === "customer";
  const waUnread = useWhatsAppUnread();

  // Customers are off the role ladder entirely — hasMinRole('customer', …) is
  // false for every rank, which would leave them an empty sidebar. They are
  // not junior staff but the owner of their own space, so rank doesn't apply:
  // they see everything except what the tenant keeps to itself.
  const visible = (i: NavItem) =>
    i.superuserOnly
      ? !!user?.is_superuser
      : isCustomer
        ? !i.tenantOnly
        : !i.customerOnly && hasMinRole(role, i.minRole);

  return (
    <aside
      className={cn(
        "hidden h-svh shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground transition-[width] duration-200 md:flex",
        collapsed ? "w-[68px]" : "w-64",
      )}
    >
      <div className="flex h-16 items-center gap-2.5 px-4">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-pink-500 text-sm font-bold text-white">
          {env.platformName.charAt(0)}
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{env.platformName}</div>
            {user?.tenant?.name && (
              <div className="truncate text-xs text-muted-foreground">
                {user.tenant.name}
              </div>
            )}
          </div>
        )}
      </div>

      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 py-2">
        {NAV_GROUPS.map((group, gi) => {
          const items = group.items.filter(visible);
          if (items.length === 0) return null;
          return (
            <div key={group.label ?? `group-${gi}`} className={cn(gi > 0 && "mt-3")}>
              {group.label && !collapsed && (
                <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {group.label}
                </div>
              )}
              <div className="flex flex-col gap-1">
                {items.map((item) => (
                  <SidebarLink
                    key={item.path}
                    item={item}
                    collapsed={collapsed}
                    badge={item.path === "/conversations" ? waUnread : 0}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="border-t px-2 py-2">
        {SECONDARY_NAV.filter(visible).map((item) => (
          <SidebarLink key={item.path} item={item} collapsed={collapsed} />
        ))}
      </div>
    </aside>
  );
}

function SidebarLink({
  item,
  collapsed,
  badge = 0,
}: {
  item: NavItem;
  collapsed: boolean;
  badge?: number;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          isActive &&
            "bg-sidebar-accent text-sidebar-accent-foreground after:absolute after:left-0 after:top-1.5 after:bottom-1.5 after:w-0.5 after:rounded-full after:bg-foreground",
          collapsed && "justify-center px-2",
        )
      }
    >
      <Icon className="size-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
      {badge > 0 &&
        (collapsed ? (
          <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-emerald-500" />
        ) : (
          <span className="ml-auto flex min-w-[18px] shrink-0 items-center justify-center rounded-full bg-emerald-500 px-1 py-0.5 text-[10px] font-semibold leading-none text-white">
            {badge > 99 ? "99+" : badge}
          </span>
        ))}
    </NavLink>
  );
}
